import { useGetRequestedLogisticsQuotesQuery } from "../../../store/apiSlices/logistics";
import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { getRFQStatusStyles } from "../../Admin/CustomerRFQsDetails/RFQCard";
import { AirPlaneIcon, MapPinIcon, ShipIcon, SpinnerIcon } from "../../Icons";
import QuoteCard from "./QuoteCard";
import { RFQSkeletonList } from "./RFQs";

export default function History({
  onSelect,
}: {
  onSelect: (item: any) => void;
}) {
  const { data, isLoading, isError, refetch, isFetching } =
    useGetRequestedLogisticsQuotesQuery();

  if (isLoading) return <RFQSkeletonList />;

  if (isError)
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        <p className="text-sm text-gray-500">Failed to load quotes.</p>

        <button
          onClick={refetch}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFetching && (
            <SpinnerIcon className="size-3 text-gray-200 animate-spin fill-gray-600" />
          )}
          Try again
        </button>
      </div>
    );

  const historyStatuses: LogisticsRequestQuote["status"][] = [
    "completed",
    "inactive",
    "denied",
  ];

  const historyQuotes =
    data?.filter((el) => el.status && historyStatuses.includes(el.status)) ||
    [];

  if (!historyQuotes.length)
    return (
      <p className="text-center text-sm text-gray-400">No history found</p>
    );

  return (
    <div className="space-y-3">
      {historyQuotes.map((quote) => (
        <QuoteCard
          key={quote.id || quote.quotes_id}
          quote={quote}
          onSelect={onSelect}
        >
          <QuoteCard.Header>
            <QuoteCard.FromTo />
            <QuoteCard.Status />
          </QuoteCard.Header>
          <QuoteCard.Body>
            <QuoteCard.FreightInfo />
            <QuoteCard.PickupDate />
          </QuoteCard.Body>
          <QuoteCard.Footer>
            <QuoteCard.CreatedAt />
          </QuoteCard.Footer>
        </QuoteCard>
      ))}
    </div>
  );
}

/* Expanded view for a single Draft item */
History.Expanded = function HistoryExpanded({
  selectedQuoteId,
  onClose,
}: {
  selectedQuoteId: LogisticsRequestQuote["id"];
  onClose: () => void;
}) {
  const { data } = useGetRequestedLogisticsQuotesQuery();

  const selectedQuote = data?.find((q) => q.id === selectedQuoteId);

  if (!selectedQuote) return null;

  const {
    from_json,
    to_json,
    freight_type,
    // loading_date,
    pickup_date,
    goods_category,
    weight,
    status,
    incoterm,
    actual_quotes,
  } = selectedQuote;

  const FreightIcon =
    freight_type?.toLowerCase() === "air" ? AirPlaneIcon : ShipIcon;
  const { label, className: statusColor } = getRFQStatusStyles(status);

  const completedQuote = actual_quotes?.find((q) => q.status === "confirmed");

  return (
    <div className="card h-full">
      <button onClick={onClose} className="mb-2 text-sm text-blue-500">
        ← Back
      </button>

      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MapPinIcon className="size-4 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-800">
              {from_json?.name} → {to_json?.name}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FreightIcon className="size-4 text-blue-500" />
            <span>{freight_type?.toUpperCase()} Freight</span>
            <span className="text-gray-400">|</span>
            <span>{weight || "—"} kg</span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span>
              <span className="font-medium text-gray-600">Incoterm:</span>{" "}
              <span className="italic">
                {incoterm ? incoterm.toUpperCase() : "—"}
              </span>
            </span>

            <span className="text-gray-300">|</span>

            <span>
              <span className="font-medium text-gray-600">Goods:</span>{" "}
              <span className="text-gray-600">
                {goods_category || "Not specified"}
              </span>
            </span>
          </div>

          <p className="text-xs text-gray-400 mt-1">
            Pickup: {pickup_date || "—"}
          </p>
        </div>
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium h-fit ${statusColor}`}
        >
          {label || "Pending"}
        </span>
      </div>

      {completedQuote && (
        <div className="border-t border-gray-100 pt-6 mt-4">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6 shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 text-emerald-600 rounded-full p-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Transaction Completed
                  </h3>
                  <p className="text-xs text-gray-500">
                    This quote has been successfully finalized.
                  </p>
                </div>
              </div>

              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                Completed
              </span>
            </div>

            {/* Details */}
            {completedQuote && (
              <div className="flex justify-between items-center border-t border-emerald-200 pt-4 mt-4">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {completedQuote.vendor}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Final agreed vendor
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-lg font-bold text-emerald-600">
                    ${completedQuote.price?.toLocaleString()}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    Final transaction amount
                  </p>
                </div>
              </div>
            )}

            {/* Footer message */}
            <div className="mt-5 text-sm text-gray-600">
              The transaction has been archived and is now available in your
              history records.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
